import type { ErrorEvent, EventHint } from '@sentry/react';
import { isAxiosError } from 'axios';

/**
 * Sentry noise filters (ND-5676).
 *
 * The free plan gives 5k error events a month and the webapp used to burn through them in
 * ~2 days, ~97% of which were console.warn noise (third-party scripts, expected API
 * failures, loading-state artefacts). Everything below is about keeping the quota for
 * errors that are actually actionable.
 */

/** HTTP statuses that are an expected outcome of a user action, never an app defect. */
const EXPECTED_HTTP_STATUSES = new Set([400, 401, 403, 404, 409, 422]);

/** Axios error codes raised by the network / the browser, not by our code. */
const NETWORK_ERROR_CODES = new Set(['ERR_NETWORK', 'ECONNABORTED', 'ERR_CANCELED', 'ETIMEDOUT']);

/**
 * Messages we never want to see: third-party console output that reaches Sentry through
 * captureConsoleIntegration, and well-known browser noise.
 */
export const ignoreErrors: (string | RegExp)[] = [
  // Legacy login flow failure, handled by the app
  'An error occurred Error status 403 at https://app.jooxter.com/v3/login',
  // Jimo SDK console output
  /^JIMO\b/i,
  /Jimo has already been preloaded/i,
  // Recharts ResponsiveContainer measured before layout
  /The width\(-?\d+\) and height\(-?\d+\) of chart should be greater than 0/,
  // Browser noise
  /^ResizeObserver loop/,
  /^Non-Error promise rejection captured/,
  // Network-level failures surfaced as plain messages
  /^Network Error$/,
  /^Load failed$/,
  /^Failed to fetch$/,
];

/** Errors thrown from browser extensions or third-party scripts are not ours to fix. */
export const denyUrls: (string | RegExp)[] = [
  /^chrome-extension:\/\//,
  /^moz-extension:\/\//,
  /^safari-extension:\/\//,
  /^safari-web-extension:\/\//,
  /undercity\.usejimo\.com/,
  /googletagmanager\.com/,
  /google-analytics\.com/,
];

/**
 * Last-chance filter on the original thrown value (which ignoreErrors cannot inspect): drops
 * API failures that are an expected part of the product flow and pure network failures.
 */
export const beforeSend = (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
  const original = hint.originalException;

  if (isAxiosError(original)) {
    const status = original.response?.status;

    if (status !== undefined && EXPECTED_HTTP_STATUSES.has(status)) {
      return null;
    }

    if (original.code && NETWORK_ERROR_CODES.has(original.code)) {
      return null;
    }
  }

  return event;
};
